import ArrayHelper from '@/classes/ArrayHelper'
import type { MenuItemsSnapshotGroupedByElement } from '@/types/contract'

export const amountsEqual = (a: number, b: number) => {
  return Math.abs(a - b) < 0.0001
}

export const sumProperties = (objects: object[], property: string) => {
  let sum = 0
  objects.forEach(object => (sum += object[property as keyof object]))
  return sum
}

/**
 * Takes in a list of menu item associations entities and
 * sums the total price taking into account custom price or district price and quantity
 */
export const sumMenuItemTotal = (
  menuItems: {
    amount?: string | null
    menuItem: { price: string } | null | undefined
    quantity?: string
  }[]
): number => {
  return menuItems.reduce((sum, menuItem) => sum + parseTotalCost(menuItem), 0)
}

/**
 * A slot with no menu item is a schedule-only session (an athletics practice):
 * it is never a charge, so it contributes nothing to a total (DTA-799).
 */
export const isScheduleOnlyItem = (item: { menuItem?: unknown } | null | undefined): boolean =>
  item === null || item === undefined || item.menuItem === null || item.menuItem === undefined

export const parseTotalCost = (menuItem: {
  amount?: string | null
  menuItem: { price: string } | null | undefined
  quantity?: string
}): number => {
  // Check if 'amount' exists and is not null or undefined
  if (
    menuItem.hasOwnProperty('amount') &&
    menuItem.amount !== null &&
    menuItem.amount !== undefined
  ) {
    return parseFloat(menuItem.amount)
  } else if (isScheduleOnlyItem(menuItem)) {
    return 0
  } else {
    const total =
      parseFloat(menuItem.menuItem!.price) *
      (menuItem.hasOwnProperty('quantity') ? parseFloat(menuItem.quantity!) : 1)
    return Number.isNaN(total) ? 0 : total
  }
}

export const sumPriceAndQuantity = (array: MenuItemsSnapshotGroupedByElement[]): number => {
  return ArrayHelper.sum(
    array.map(item => item.price * (item.hasOwnProperty('quantity') ? item.quantity! : 1))
  )
}

export const formatCurrency = (amount: number) => {
  if (amount < 0) {
    return (
      '-$' +
      Math.abs(amount)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    )
  } else {
    return (
      '$' +
      amount
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    )
  }
}

/**
 * @param {string} string
 */
export const capitalize = (string: string) => {
  let lower = string.toLowerCase()
  return lower.replace(/^\w/, char => char.toUpperCase())
}

/**
 * @param {Date} date
 * @returns {string}
 */
export const formatDate = (date: Date) => {
  let month = '' + (date.getMonth() + 1)
  let day = '' + date.getDate()
  let year = date.getFullYear()

  if (month.length < 2) month = '0' + month
  if (day.length < 2) day = '0' + day

  return [year, month, day].join('-')
}

/**
 * Remove time from date time string
 * @param datetimeString
 */
export const removeTimestamp = (datetimeString: string) => {
  return datetimeString.split(' ')[0]
}

/**
 * Formats a 'Y-m-d' datestring to the US 'm/d/Y' format.
 * @param {string} dateString
 * @returns {string}
 */
export const formatUSDateString = (dateString: string) => {
  const [year, month, day] = dateString.split('-')
  return [month, day, year].join('/')
}

export const formatDateShort = (date: Date) => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ]
  const month = months[date.getMonth()]
  const day = date.getDate()
  const year = date.getFullYear()
  return `${month} ${day}, ${year}`
}

export const dateStringToObj = (dateString: string) => {
  const [year, month, day] = dateString.split('-')
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
}

/**
 * Converts a month name and week number into start and end dates for that week
 * @param {string} monthName - The name of the month (e.g. "january")
 * @param {number} weekNumber - The week number (1-5)
 * @returns {Object} An object containing startDate and endDate
 */
export const getWeekDates = (monthName: string, weekNumber: number) => {
  // Convert month name to month number (0-11)
  const monthNames = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
  ]
  const monthIndex = monthNames.indexOf(monthName.toLowerCase())

  if (monthIndex === -1) {
    throw new Error('Invalid month name')
  }

  if (weekNumber < 1 || weekNumber > 5) {
    throw new Error('Week number must be between 1 and 5')
  }

  // Get the first day of the month
  const year = new Date().getFullYear()
  const firstDayOfMonth = new Date(year, monthIndex, 1)

  // Get the first day of the week (Monday)
  const firstDayOfWeek = new Date(firstDayOfMonth)
  const dayOfWeek = firstDayOfMonth.getDay()
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1 // Adjust for Monday as first day
  firstDayOfWeek.setDate(firstDayOfMonth.getDate() - diff)

  // Calculate start and end dates for the specified week
  const startDate = new Date(firstDayOfWeek)
  startDate.setDate(startDate.getDate() + (weekNumber - 1) * 7)

  const endDate = new Date(startDate)
  endDate.setDate(endDate.getDate() + 6) // Add 6 days to get to Sunday

  return {
    startDate,
    endDate
  }
}

/**
 * Converts a date into start and end dates for that week
 * @param {Date} date - The date to convert
 * @returns {Object} An object containing startDate and endDate
 */
export const getWeekDatesFromDate = (date: Date) => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)

  const day = d.getDay()

  const monday = new Date(d)
  const diffToMonday = day === 0 ? -6 : 1 - day
  monday.setDate(d.getDate() + diffToMonday)
  monday.setHours(0, 0, 0, 0)

  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  sunday.setHours(0, 0, 0, 0) // end of day

  return {
    startDate: monday,
    endDate: sunday
  }
}

/**
 * Determines which week of the month a given date falls into (1-4)
 * @param {Date} date - The date to check
 * @returns {number} The week number (1-4) of the month
 */
export const getWeekNumber = (date: Date): number => {
  // Get the first day of the month
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1)

  // Get the first day of the week (Monday)
  const firstDayOfWeek = new Date(firstDayOfMonth)
  const dayOfWeek = firstDayOfMonth.getDay()
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1 // Adjust for Monday as first day
  firstDayOfWeek.setDate(firstDayOfMonth.getDate() - diff)

  // Calculate the week number
  const daysDiff = Math.floor((date.getTime() - firstDayOfWeek.getTime()) / (1000 * 60 * 60 * 24))
  const weekNumber = Math.floor(daysDiff / 7) + 1

  // Ensure the week number is between 1 and 4
  return Math.min(Math.max(weekNumber, 1), 4)
}

/**
 * Add days to date object.
 * @param {Date} date
 * @param {number} days
 * @returns {Date}
 */
export const addDays = (date: Date, days: number) => {
  const newDate = new Date(date)
  newDate.setDate(newDate.getDate() + days)
  return newDate
}

/**
 * Subtract days from date object.
 * @param {Date} date
 * @param {number} days
 * @returns {Date}
 */
export const subtractDays = (date: Date, days: number) => {
  const newDate = new Date(date)
  newDate.setDate(newDate.getDate() - days)
  return newDate
}

/**
 * Converts 24-hour time format to 12-hour AM/PM format
 * @param {string} time24 - Time in 24-hour format (e.g., "09:00:00" or "15:30:00")
 * @returns {string} Time in 12-hour format (e.g., "9:00 AM" or "3:30 PM")
 */
export const formatTime12Hour = (time24: string): string => {
  if (!time24) return ''

  // Split the time string to get hours and minutes
  const [hours24, minutes] = time24.split(':')
  const hour24 = parseInt(hours24, 10)

  // Determine AM/PM
  const period = hour24 >= 12 ? 'PM' : 'AM'

  // Convert to 12-hour format
  let hour12 = hour24 % 12
  if (hour12 === 0) hour12 = 12 // Handle midnight (0) and noon (12)

  return `${hour12}:${minutes} ${period}`
}

/**
 * Converts 24-hour time format to 12-hour AM/PM format
 * @param {string} time24 - Time in 24-hour format (e.g., "09:00:00" or "15:30:00")
 * @returns {string} Time in 12-hour format (e.g., "9:00 AM" or "3:30 PM")
 */
export const formatTime12HourShort = (time24: string): string => {
  if (!time24) return ''

  // Split the time string to get hours and minutes
  const [hours24, minutes] = time24.split(':')
  const hour24 = parseInt(hours24, 10)

  // Determine AM/PM
  const period = hour24 >= 12 ? 'pm' : 'am'

  // Convert to 12-hour format
  let hour12 = hour24 % 12
  if (hour12 === 0) hour12 = 12 // Handle midnight (0) and noon (12)

  if (minutes === '00') {
    return `${hour12} ${period}`
  }
  return `${hour12}:${minutes} ${period}`
}

/**
 * Converts a day number to its ordinal suffix (st, nd, rd, th)
 * @param {number} day - The day number to convert
 * @returns {string} The ordinal suffix (e.g., "1st", "2nd", "3rd", "4th", etc.)
 */
export const getOrdinalSuffix = (day: number) => {
  if (day % 100 >= 11 && day % 100 <= 13) return `${day}th`
  switch (day % 10) {
    case 1:
      return `${day}st`
    case 2:
      return `${day}nd`
    case 3:
      return `${day}rd`
    default:
      return `${day}th`
  }
}
